import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import { getLocales } from 'assets/locales/Locale';
import ObjectInfo from 'assets/templates/objectInfo/ObjectInfo';
import ValueDisplay from 'assets/templates/valueDisplay/ValueDisplay';
import { date, defaultDateFormat, defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { isEmpty, pick, values } from 'lodash';
import PurchaseState from 'models/enums/PurchaseState';
import Contract from 'models/sales/contract/Model.Contract';
import ContractRefundPriceSummariesTable from '../../components/ContractRefundPriceSummariesTable';
import { RefundDistributionTable, RefundSubfeeTable } from '../../components/RefundDistributionTable';
import { useContractRefundProvider } from '../../Sales.ContractRefund.Hooks';

const SalesContractRefundInfoGeneralInfo = () => {
  const { lang, translate } = getLocales();
  const { selected, contract, vehicle, contractCancellation } = useContractRefundProvider();

  if (!selected) return null;

  return (
    <>
      <ObjectInfo
        object={pick(
          { ...contract, totalPrice: new Contract(contract).salePriceWithTaxCAD },
          'id',
          'appNumber',
          'effectiveDate',
          'paidOnLocalDate',
          'expiryDate',
          'createdAtLocal',
          'createdAtUtc',
          'modifiedAtUtc',
          'isPackage',
          'postSale',
          'totalPrice',
          'vertaforeStatus',
          'isContractTransferred'
        )}
        objectName={lang.contract}
      />
      &nbsp;
      <ContractRefundPriceSummariesTable contract={contract} contractRefund={selected} />
      &nbsp;
      <div>
        <table className={ThemeTableSimple2}>
          <thead>
            <tr>
              <th>{lang.term}</th>
              <th></th>
              <th>{lang.kms}</th>
              <th></th>
              <th>{lang.profitRetention}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{lang.effectiveDate}</td>
              <td>{date(contract?.effectiveDate).format(defaultDateFormat)}</td>

              <td style={{ fontWeight: 600 }}>{lang.startingOdometerReading}</td>
              <td>{vehicle?.posOdometerReading}</td>

              <td style={{ fontWeight: 600 }}>{lang.dealerProfitAmt}</td>
              <td>{selected?.dealerProfitValueAtSale}</td>
            </tr>
            <tr>
              <td>{lang.cancellationDate}</td>
              <td>{date(contractCancellation?.cancellationDateLocal).format(defaultDateFormat)}</td>

              <td style={{ fontWeight: 600 }}>{lang.currentOdometerReading}</td>
              <td>{contractCancellation?.odometerReading}</td>

              <td style={{ fontWeight: 600 }}>{lang.dealerProtectProfitExpirationDays}</td>
              <td>{selected?.refundConfigTemplateJson?.dealerProtectProfitExpirationDays}</td>
            </tr>
            <tr>
              <td>{lang.expiryDate}</td>
              <td>{date(contract?.expiryDate).format(defaultDateFormat)}</td>

              <td style={{ fontWeight: 600 }}>{lang.maxCoverageKMUsedInDP}</td>
              <td>{contract?.maxCoverageKm}</td>

              <td style={{ fontWeight: 600 }}>{lang.dealerProtectedProfitExpirationDay}</td>
              <td>{0}</td>
            </tr>
            <tr>
              <td>{lang.termInDays}</td>
              <td>{selected?.termInDays}</td>

              <td style={{ fontWeight: 600 }}>{lang.termInKM}</td>
              <td>{selected?.termInKms}</td>

              <td style={{ fontWeight: 600 }}>{lang.protectedDealerProfitAmt}</td>
              <td>{selected?.refundConfigTemplateJson?.dealerProtectProfitAmount}</td>
            </tr>
            <tr>
              <td>{lang.termDaysConsumed}</td>
              <td>{selected?.usedTermInDays}</td>

              <td style={{ fontWeight: 600 }}>{lang.kmsUsedConsumed}</td>
              <td>{selected?.usedOdometerReading}</td>

              <td style={{ fontWeight: 600 }}>{lang.unprotectedDealerProfitAmt}</td>
              <td>{selected?.unprotectedDealerProfitAmt}</td>
            </tr>
            <tr>
              <td>{lang.termDaysRemaining}</td>
              <td>{selected?.remainingTermInDays}</td>

              <td style={{ fontWeight: 600 }}>{lang.kmsRemaining}</td>
              <td>{selected?.remainingOdometerReading}</td>

              <td style={{ fontWeight: 600 }}>{lang.retainFactor}</td>
              <td>{selected?.retainFactor}</td>
            </tr>
            <tr>
              <td>{lang.percentOfTermConsumed}</td>
              <td>{selected?.usedTermInDaysPercentage * 100}%</td>

              <td style={{ fontWeight: 600 }}>{lang.percentOfKMConsumed}</td>
              <td>{selected?.usedOdometerReadingPercentage * 100}%</td>

              <td style={{ fontWeight: 600 }}>{lang.refundFactor}</td>
              <td>{selected?.refundFactor}</td>
            </tr>
            <tr>
              <td>{lang.percentOfTermRemaining}</td>
              <td>{selected?.remainingTermInDaysPercentage * 100}%</td>

              <td style={{ fontWeight: 600 }}>{lang.percentOfKMRemaining}</td>
              <td>{selected?.remainingOdometerReadingPercentage * 100}%</td>

              <td style={{ fontWeight: 600 }}>{lang.refundFactorAfterMinimumRetained}</td>
              <td>{0}</td>
            </tr>
          </tbody>
        </table>
      </div>
      &nbsp;
      <h1>{lang.vehicle}</h1>
      <div>
        {lang.purchaseState}:{' '}
        <b>
          {translate(vehicle?.purchaseState === PurchaseState.USED ? PurchaseState.PRE_OWNED : vehicle?.purchaseState)}
        </b>
      </div>
      <div>
        {lang.purchaseType}: <b>{translate(vehicle?.purchaseType) ?? 'N/A'}</b>
      </div>
      <div>
        {lang.startingOdometer}:{' '}
        <b>
          <ValueDisplay value={`${vehicle?.posOdometerReading} km`} />
        </b>
      </div>
      <div>
        {lang.maxCoverageKm}:{' '}
        <b>
          <ValueDisplay value={`${contract?.maxCoverageKm ?? 0} km`} />
        </b>
      </div>
      <div>
        {lang.kmsUsed}:{' '}
        <b>
          <ValueDisplay value={`${selected?.usedOdometerReading} km`} />
        </b>
      </div>
      <div>
        {lang.kmConsumed}:{' '}
        <b>
          <ValueDisplay value={`${selected?.usedOdometerReadingPercentage ?? 0} %`} />
        </b>
      </div>
      <div>
        {lang.kmRemaining}:{' '}
        <b>
          <ValueDisplay value={`${selected?.remainingOdometerReadingPercentage ?? 0} %`} />
        </b>
      </div>
      &nbsp; <h3>{lang.termAndExpiryInformation}</h3>
      <div>
        {lang.effectiveDate}: <b>{localizeDate(contract?.effectiveDate, defaultDateTimeFormat)}</b>
      </div>
      <div>
        {lang.expiryDate}: <b>{localizeDate(contract?.expiryDate, defaultDateTimeFormat)}</b>
      </div>
      <div>
        {lang.termInDays}: <b>{selected?.termInDays}</b>
      </div>
      <div>
        {lang.daysConsumed}: <b>{selected?.usedTermInDays}</b>
      </div>
      <div>
        {lang.daysRemaining}: <b>{selected?.remainingTermInDays}</b>
      </div>
      <div>
        {lang.termConsumed}: <b>{selected?.usedTermInDaysPercentage}</b>
      </div>
      <div>
        {lang.termRemaining}: <b>{selected?.remainingTermInDaysPercentage}</b>
      </div>
      &nbsp;
      <div>
        <h1>{lang.contractPricing}</h1>
        <div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h3>{lang.dealerCost}</h3>
            <div>
              {lang.paymentPrice}:{' '}
              <b>
                <FormatValue
                  value={`$${contract?.originalPriceCAD + contract?.discountPriceCAD + contract?.distributorPriceCAD}`}
                />
              </b>
            </div>
            <div>
              {lang.enhacementsPriceCAD}:{' '}
              <b>
                <FormatValue value={`$${contract?.enhancementsPriceCAD}`} />
              </b>
            </div>
            <div>
              {lang.surchargesPriceCAD}:{' '}
              <b>
                <FormatValue value={`$${contract?.surchargesPriceCAD}`} />
              </b>
            </div>
            <div>
              {lang.paymentTaxes}:{' '}
              <b>
                <FormatValue value={`$${contract?.dealerCostTaxAmt}`} />
              </b>
            </div>
            <hr />
            <div>
              {lang.totalPriceAmountWPaymentTaxes}:{' '}
              <b>
                <FormatValue value={`$${contract?.dealerCostTotal}`} />
              </b>
            </div>
          </div>
          <div>
            <h3>{lang.salePrice}</h3>
            <div>
              {lang.salePrice}:{' '}
              <b>
                <FormatValue value={`$${contract?.salePriceCAD}`} />
              </b>
            </div>
            <div>
              {lang.salePriceTaxes}:{' '}
              <b>
                <FormatValue value={`$${contract?.totalTaxCAD}`} />
              </b>
            </div>
            <hr />
            <div>
              {lang.totalRetailPriceAmountWSalePriceTaxes}:{' '}
              <b>
                <FormatValue value={`$${contract?.salePriceWithTaxCAD}`} />
              </b>
            </div>
          </div>

          <div>
            <h3>{lang.dealerProfitNonASC}</h3>
            <div>
              {lang.dealerProfit}:{' '}
              <b>
                <FormatValue value={`$${contract?.dealerProfit}`} />
              </b>
            </div>
            <div>
              {lang.dealerProfitTax}:{' '}
              <b>
                <FormatValue value={`$${contract?.dealerProfitTaxAmt}`} />
              </b>
            </div>
            <hr />
            <div>
              {lang.totalDealerProfitWDealerProfitTaxes}:{' '}
              <b>
                <FormatValue value={`$${contract?.dealerProfitWithTaxCAD}`} />
              </b>
            </div>
          </div>
        </div>
      </div>
      &nbsp;
      <h1>{lang.distributions}</h1>
      {!!values(selected?.pricingDistributions).length && (
        <RefundDistributionTable title={lang.pricingDistributions} distributions={selected?.pricingDistributions} />
      )}
      {!!values(selected?.distributorDistributions).length && (
        <RefundDistributionTable
          title={lang.distributorDistributions}
          distributions={selected?.distributorDistributions}
        />
      )}
      &nbsp;
      {selected?.subfees && !isEmpty(selected?.subfees) && (
        <>
          <h1>{lang.surcharges}</h1>
          <RefundSubfeeTable subfees={selected?.subfees} />
        </>
      )}
      {selected?.enhancements && !isEmpty(selected?.enhancements) && (
        <>
          <h1>{lang.enhancements}</h1>

          <DataMap
            data={selected?.enhancements}
            render={({ data: enhancement }) => {
              return (
                <div key={enhancement?.enhancementId}>
                  <h2>{enhancement.name || lang.unknown}:</h2>

                  {!!values(enhancement?.pricingDistributions).length && (
                    <RefundDistributionTable
                      title={lang.pricingDistributions}
                      distributions={enhancement?.pricingDistributions}
                    />
                  )}
                  {!!values(enhancement?.distributorDistributions).length && (
                    <RefundDistributionTable
                      title={lang.distributorDistributions}
                      distributions={enhancement?.distributorDistributions}
                    />
                  )}
                </div>
              );
            }}
          />
        </>
      )}
      &nbsp;
      {!isEmpty(selected?.contractRefundPayees) && (
        <>
          <h1>{lang.payees}</h1>
          {selected?.contractRefundPayees?.map((it, index) => {
            const source = it.clientId ? it.client : it.dealerId ? it.dealer : it.lenderId ? it.lender : it.payee;
            return (
              <ObjectInfo
                key={it.id}
                object={pick(
                  {
                    ...(source || {}),
                    paymentMethodCode: it.dealerId
                      ? (it.dealer?.paymentMethodCode ?? it.paymentMethodCode)
                      : it.paymentMethodCode,
                  },
                  'firstName',
                  'lastName',
                  'name',
                  'email',
                  'phone',
                  'streetAddress',
                  'city',
                  'provinceCode',
                  'postalCode',
                  'taxExempt',
                  'paymentMethodCode'
                )}
                objectName={it.isPrimary ? `${lang.primaryPayee} (${lang.requestor})` : `${lang.payee} #${index}`}
              />
            );
          })}
        </>
      )}
    </>
  );
};

export default SalesContractRefundInfoGeneralInfo;
